import { YupMessage } from 'constants/index';
import {
  transformFn,
  percentageSchema,
  isContactNumber,
  userPrincipalSchema,
  twoDigitsValidationSchema,
  isEmail
} from 'helpers/utils';
import yup from 'validation.yup';

const CARD_ORIGIN_RATES_REQUIRED =
  'Set both rates before turning card origin pricing on. A zero here pays the fleet the full fare.';

// The API skips the card-origin adjustment on a zero rate, so an enrolled
// company without both rates would silently keep the original split.
const cardOriginRateSchema = yup
  .number()
  .transform(transformFn)
  .concat(twoDigitsValidationSchema)
  .required(CARD_ORIGIN_RATES_REQUIRED)
  .notOneOf([0], CARD_ORIGIN_RATES_REQUIRED);

const cardOriginFeeTypeSchema = yup.number().transform(transformFn).oneOf([1, 2]).required();

// The rates are only checked while card-origin pricing is in effect, which the
// form passes as the cardOriginEffective validation context.
const whenCardOriginEffective = schema =>
  yup.mixed().when('$cardOriginEffective', {
    is: true,
    then: () => schema
  });

export const CARD_ORIGIN_RATE_FIELDS = [
  'stripe_domestic_fee',
  'stripe_domestic_fee_type',
  'stripe_international_fee',
  'stripe_international_fee_type'
];

export const genCompanyFormSchema = isAssignExistUser =>
  yup
    .object({
      name: yup.string().required(),
      email: yup.lazy(value => {
        if (!value) return yup.string().default('').nullable();
        return yup.string().matches(isEmail, YupMessage.INVALID_EMAIL);
      }),
      tax_number: yup.string(),
      receipt_contact_info: yup.string().required(),
      contact_number: yup.lazy(value => {
        if (!value) return yup.string().default('').nullable();
        return yup.string().matches(isContactNumber, YupMessage.INVALID_FORMAT);
      }),
      address: yup.object().shape({
        postcode: yup.string().required(),
        city: yup.string().required(),
        country_id: yup.number().transform(transformFn).required(),
        detail_address: yup.string().required()
      }),
      timezone_id: yup.number().transform(transformFn).required(),
      company_meter_connection_id: yup.number().transform(transformFn).required(),
      company_meter_client_id: yup.number().transform(transformFn).nullable(),
      group_id: yup.number().transform(transformFn).required(),
      company_comm: yup
        .number()
        .concat(percentageSchema)
        .concat(twoDigitsValidationSchema)
        .when('group_comm', (groupRate, schema) =>
          schema.max(parseFloat(groupRate, 10) || 0, YupMessage.NOT_GREATER_THAN_GROUP_RATE)
        )
        .required(),
      payment_point: yup.string().required(),
      stripe_domestic_fee: whenCardOriginEffective(cardOriginRateSchema),
      stripe_domestic_fee_type: whenCardOriginEffective(cardOriginFeeTypeSchema),
      stripe_international_fee: whenCardOriginEffective(cardOriginRateSchema),
      stripe_international_fee_type: whenCardOriginEffective(cardOriginFeeTypeSchema),
      ...(isAssignExistUser
        ? { existing_principal: yup.object().required() }
        : { user_principal: yup.object().concat(userPrincipalSchema) })
    })
    .required();
